import { useCallback, useRef, useSyncExternalStore } from 'react'

/* eslint-disable react-hooks/refs --
 * Write-once lazy ref init (React-sanctioned): the fallback is resolved at most
 * once per instance and only when the stored key is absent/unparseable (asserted
 * in the tests), never recomputed on re-render. The compiler rule over-flags this
 * read-during-render — it is not a render-purity bug. */

/**
 * `useState` for a localStorage value. Reads on mount, writes on every set,
 * and stays coherent across tabs (via the native `storage` event) and across
 * hook instances on the same tab (via an in-process bus, because `storage`
 * doesn't fire in the originating tab).
 *
 * `parse` returns `undefined` to fall through to `initial` on corrupt data.
 * A null key uses `initial` without reading or writing browser storage.
 */
export function useLocalStorageState<T>(
  key: string | null,
  parse: (raw: string) => T | undefined,
  serialize: (value: T) => string,
  initial: T | (() => T),
): [T, (value: T | ((prev: T) => T)) => void] {
  const subscribe = (listener: () => void) => {
    if (key === null) return () => undefined
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === localStorage && event.key === key) {
        listener()
      }
    }
    window.addEventListener('storage', onStorage)
    bus.add(key, listener)
    return () => {
      window.removeEventListener('storage', onStorage)
      bus.delete(key, listener)
    }
  }

  const getSnapshot = () => key === null ? null : localStorage.getItem(key)
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  // Resolve the lazy `initial` at most once per instance, and only when
  // needed (key absent or unparseable). Recomputing it on every such render
  // was the bug.
  const fallbackRef = useRef<{ value: T } | null>(null)
  const fallback = () => {
    fallbackRef.current ??= {
      value: typeof initial === 'function' ? (initial as () => T)() : initial,
    }
    return fallbackRef.current.value
  }

  const resolve = (stored: string | null): T => {
    if (stored === null) return fallback()
    const parsed = parse(stored)
    return parsed !== undefined ? parsed : fallback()
  }

  const value = resolve(raw)

  // Accepts a value or an updater. The updater is resolved against the *live*
  // store, not a render-time snapshot, so two writers racing on the same key
  // (another tab, or a concurrent in-flight write) can't clobber each other.
  const resolveRef = useRef(resolve)
  resolveRef.current = resolve
  const serializeRef = useRef(serialize)
  serializeRef.current = serialize

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (key === null) return
      const resolved =
        typeof next === 'function'
          ? (next as (prev: T) => T)(resolveRef.current(localStorage.getItem(key)))
          : next
      localStorage.setItem(key, serializeRef.current(resolved))
      bus.notify(key)
    },
    [key],
  )

  return [value, setValue]
}

const bus = (() => {
  const listeners = new Map<string, Set<() => void>>()
  return {
    add(key: string, fn: () => void) {
      let set = listeners.get(key)
      if (!set) {
        set = new Set()
        listeners.set(key, set)
      }
      set.add(fn)
    },
    delete(key: string, fn: () => void) {
      const set = listeners.get(key)
      if (!set) return
      set.delete(fn)
      if (set.size === 0) listeners.delete(key)
    },
    notify(key: string) {
      const set = listeners.get(key)
      if (!set) return
      for (const fn of set) fn()
    },
  }
})()
